export function isFalsyOrWhitespace(input: string | null | undefined): boolean {
    return !input || input.trim() === '';
}

export function routeMatchesRouteTemplate(
    route: string,
    routeTemplate: string,
): boolean {
    const regex = new RegExp(
        `^${routeTemplate.replace(':id', '\\d+')}(\\?.*)?$`,
    );
    return route.match(regex) !== null;
}

/**
 * A strongly-typed way to get a property of a class.
 * @param name of the property.
 * @returns name of the property.
 */
export const nameOf = <T>(name: Extract<keyof T, string>): string => name;

/**
 * A strongly-typed way to get the name of a class.
 * @returns The name of the class.
 */
export function nameOfClass<T>(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    classConstructor: new (...args: any[]) => T,
): string {
    return classConstructor.name;
}

/**
 * Tries to parse a string to a number.
 * @param value
 * @returns The number or null if the value is not a number.
 */
export function tryParseToNumber(
    value: string | null | undefined,
): number | null {
    if (isFalsyOrWhitespace(value)) {
        return null;
    }

    const num = Number(value);
    return isNaN(num) ? null : num;
}

/**
 * Replaces pipe characters with newline characters in text.
 * @param text The input text containing pipe delimiters
 * @returns Text with pipes replaced by newlines
 */
export function replacePipeWithNewline(text: string): string {
    return text.replace(/\|/g, '\n');
}

/**
 * Compares two strings case-insensitively.
 * Returns false if either string is null or undefined.
 * @param a First string to compare
 * @param b Second string to compare
 * @returns true if both strings are defined and match case-insensitively, false otherwise
 */
export function areEqualIgnoreCase(
    a: string | null | undefined,
    b: string | null | undefined,
): boolean {
    if (a == null || b == null) {
        return false;
    }
    return a.toUpperCase() === b.toUpperCase();
}

/**
 * All FRN numbers must display as 10 characters. If the FRN number is less than 10 characters,
 * this function will make sure to add leading zeroes.
 * @param fccRegistrationNumber The FRN number to pad
 * @returns The padded FRN number (if needed), or null if input is null/undefined
 */
export function padFrnWithLeadingZeros(
    fccRegistrationNumber: string | null | undefined,
): string | null {
    if (!fccRegistrationNumber || fccRegistrationNumber.length === 10) {
        return fccRegistrationNumber ?? null;
    }

    return fccRegistrationNumber.padStart(10, '0');
}

/**
 * Converts the first letter of each word in the input text to uppercase and the rest to lowercase.
 * If the input text is null, undefined, or empty, it returns an empty string.
 *
 * @param text The input text to titleize
 * @returns The titleized text or an empty string if input is null, undefined, or empty
 */
export function titleize(text: string | null | undefined): string {
    if (!text) {
        return '';
    }

    return text
        .split(' ')
        .map(
            (word) =>
                word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
        )
        .join(' ');
}
